import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // GA4 property (same as marketing). Needed by getAnalytics(); analytics is
  // initialised lazily + consent-gated in lib/analytics.ts, not here.
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// App Check: initialise only when a reCAPTCHA v3 site key is configured, so
// the app still runs in dev and before App Check is registered in the Firebase
// console. Once tokens are flowing here, enable backend enforcement
// (enforce_app_check on the callables) — see docs/security-hardening.md.
// initializeAppCheck must run before getFunctions() so callables attach tokens.
const appCheckSiteKey = import.meta.env.VITE_APPCHECK_RECAPTCHA_KEY;
if (appCheckSiteKey) {
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    // Dev-only: lets a non-reCAPTCHA origin (localhost / preview) pass App
    // Check using a debug token registered in the console.
    (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
// In-memory Firestore cache — no IndexedDB persistence. This app is online-first
// (it manages a Wi-Fi device; per-account data is small), so durable offline
// caching buys little, while the persistent IndexedDB cache caused two real
// problems we hit in the field:
//   1. Slowness — persistentLocalCache's IndexedDB layer ran ~1s per operation
//      as the cache grew, blocking the main thread across the whole app (a
//      documented SDK regression — firebase-js-sdk#7347).
//   2. Offline-write risk — writes sat in a persisted mutation queue that
//      survived reloads, so a caregiver could think a calendar event saved when
//      it had only been queued locally (and could be lost on a cache clear /
//      corruption) — unacceptable for a reminder product.
// memoryLocalCache sidesteps IndexedDB entirely, which also removes the cache-
// corruption class the old self-heal handled. Existing users' old IndexedDB
// caches are simply orphaned (never reopened) — harmless.
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
});

export const functions = getFunctions(
  app,
  import.meta.env.VITE_FUNCTIONS_REGION || 'europe-west2',
);
export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();
